// iter104 — AI Feedback Dashboard for President + VP.
// Browse wrong-slice reports filed by Tender Executives so we can spot
// Gemini's blind spots (which slots miss most, which page ranges are
// wildly off) and refine the extraction prompt over time.
//
// Route: /t/<slug>/ai-feedback  (mounted in App.js, President+VP only)
// Backend: GET /api/tenders/ai-feedback  +  PATCH /api/tenders/ai-feedback/{id}

import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Sparkle, CheckCircle, Circle, FileText, ArrowSquareOut, ClipboardText } from '@phosphor-icons/react';

import { TableRowsSkeleton } from '../components/Skeletons';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SLOT_LABELS = {
  emd_page: 'EMD Page',
  participation_criteria: 'Participation Criteria',
  boq_sheet: 'BOQ Sheet',
};

const authedFileUrl = (url) => {
  if (!url) return '';
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const base = url.startsWith('http') ? url : `${BACKEND_URL}${url}`;
  return token ? `${base}${base.includes('?') ? '&' : '?'}auth=${encodeURIComponent(token)}` : base;
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const fmtPages = (pages) => {
  if (!Array.isArray(pages) || pages.length === 0) return '—';
  if (pages.length === 1) return `p ${pages[0]}`;
  // iter105 fix — only render "pp X–Y" when the list is a strict contiguous
  // range. Non-contiguous lists (e.g. [12,14,16]) print verbatim so the
  // prompt-tweak snippet stays accurate.
  const isContiguous = pages.every((n, i) => i === 0 || n === pages[i - 1] + 1);
  if (isContiguous) return `pp ${pages[0]}–${pages[pages.length - 1]}`;
  return `pp ${pages.join(', ')}`;
};

// iter105 — parse "12-14" or "12,13,14" or "12" into [12,13,14] etc.
// Returns null if unparseable — caller should error with a friendly toast.
const parseCorrectPages = (raw) => {
  const trimmed = (raw || '').trim();
  if (!trimmed) return null;
  // Range form "start-end"
  const rangeMatch = trimmed.match(/^(\d+)\s*[-–]\s*(\d+)$/);
  if (rangeMatch) {
    const s = parseInt(rangeMatch[1], 10);
    const e = parseInt(rangeMatch[2], 10);
    if (s > 0 && e >= s) {
      const arr = [];
      for (let i = s; i <= e; i++) arr.push(i);
      return arr;
    }
    return null;
  }
  // Comma-separated form "12,13,14"
  const parts = trimmed.split(/[\s,]+/).filter(Boolean).map((p) => parseInt(p, 10));
  if (parts.every((n) => Number.isInteger(n) && n > 0)) return parts;
  return null;
};

// iter105 — assemble the "prompt tweak" snippet the President can paste
// into their Gemini extraction-prompt tuning notes.
const buildPromptTweak = (row, correctPages) => {
  const label = SLOT_LABELS[row.slot] || row.slot;
  const wrong = fmtPages(row.ai_pages);
  const right = fmtPages(correctPages);
  const note = (row.note || '').trim() || '(no note)';
  const reporter = row.reported_by_name ? ` by ${row.reported_by_name}` : '';
  const date = fmtDate(row.reported_at);
  return `Wrong-slice example — Slot "${label}"
  AI picked pages: ${wrong}
  Correct pages: ${right}
  Reporter note${reporter}: ${note}
  Reported at: ${date}
  Source tender: ${row.tender_doc_url || '(unknown)'}
  AI slice file: ${row.ai_file_url || '(unknown)'}

Tuning hint: In this doc, the "${label}" section actually lives at ${right}, NOT ${wrong}. Consider adding a lexical anchor / heading pattern the prompt can key on next time.
`;
};

export default function AiFeedback() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [unreviewed, setUnreviewed] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters — kept as controlled inputs, applied on submit / change.
  const [slot, setSlot] = useState('');
  const [reviewed, setReviewed] = useState('');   // '', 'true', 'false'
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [busyId, setBusyId] = useState('');

  // iter105 — inline "correct pages" capture for the Copy-as-prompt-tweak action.
  //   tweakEditingId: id of the row currently showing the input, or ''.
  //   tweakInput: controlled value of the pages input.
  //   tweakError: per-row parse/save error message.
  //   copiedId: transient — highlights which row was just successfully copied.
  const [tweakEditingId, setTweakEditingId] = useState('');
  const [tweakInput, setTweakInput] = useState('');
  const [tweakError, setTweakError] = useState('');
  const [copiedId, setCopiedId] = useState('');

  const fetchRows = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (slot) params.slot = slot;
      if (reviewed) params.reviewed = reviewed;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const res = await axios.get(`${API}/tenders/ai-feedback`, { params });
      setRows(res.data?.rows || []);
      setTotal(res.data?.total || 0);
      setUnreviewed(res.data?.unreviewed || 0);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to load feedback');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slot, reviewed, fromDate, toDate]);

  const toggleReviewed = async (row) => {
    setBusyId(row.id);
    try {
      await axios.patch(`${API}/tenders/ai-feedback/${row.id}`, { reviewed: !row.reviewed });
      // Optimistic local update
      setRows((rs) => rs.map((r) => r.id === row.id ? {
        ...r,
        reviewed: !row.reviewed,
        reviewed_at: !row.reviewed ? new Date().toISOString() : undefined,
      } : r));
      setUnreviewed((n) => Math.max(0, n + (row.reviewed ? 1 : -1)));
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Update failed');
    } finally {
      setBusyId('');
    }
  };

  // iter105 — copy the snippet to clipboard + flash the row.
  const copyTweakToClipboard = async (row, correctPages) => {
    const snippet = buildPromptTweak(row, correctPages);
    try {
      await navigator.clipboard.writeText(snippet);
    } catch {
      // Fallback for environments where clipboard API is unavailable.
      const ta = document.createElement('textarea');
      ta.value = snippet;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch { /* ignore */ }
      document.body.removeChild(ta);
    }
    setCopiedId(row.id);
    setTimeout(() => setCopiedId((c) => (c === row.id ? '' : c)), 2500);
  };

  // iter105 — click handler for "Copy as prompt tweak". If correct_pages
  // is already cached on the row, copy immediately. Otherwise open the
  // inline input on that row.
  const onCopyTweakClick = async (row) => {
    setTweakError('');
    if (Array.isArray(row.correct_pages) && row.correct_pages.length > 0) {
      await copyTweakToClipboard(row, row.correct_pages);
      return;
    }
    setTweakEditingId(row.id);
    setTweakInput('');
  };

  // iter105 — save the "correct pages" from the inline input, then copy.
  const saveTweakAndCopy = async (row) => {
    const parsed = parseCorrectPages(tweakInput);
    if (!parsed) {
      setTweakError('Use a range like "12-14" or a list like "12,13,14".');
      return;
    }
    setBusyId(row.id);
    setTweakError('');
    try {
      await axios.patch(`${API}/tenders/ai-feedback/${row.id}`, { correct_pages: parsed });
      // Optimistic cache on the row.
      setRows((rs) => rs.map((r) => r.id === row.id ? { ...r, correct_pages: parsed } : r));
      await copyTweakToClipboard({ ...row, correct_pages: parsed }, parsed);
      setTweakEditingId('');
      setTweakInput('');
    } catch (e) {
      setTweakError(e?.response?.data?.detail || e.message || 'Save failed');
    } finally {
      setBusyId('');
    }
  };

  const slotCounts = useMemo(() => {
    const c = { emd_page: 0, participation_criteria: 0, boq_sheet: 0 };
    for (const r of rows) if (c[r.slot] !== undefined) c[r.slot] += 1;
    return c;
  }, [rows]);

  const clearFilters = () => { setSlot(''); setReviewed(''); setFromDate(''); setToDate(''); };
  const hasFilter = slot || reviewed || fromDate || toDate;

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="ai-feedback-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 text-[#00a69c] font-black text-2xl" style={{ fontFamily: 'Outfit' }}>
            <Sparkle weight="fill" className="w-7 h-7" />
            AI Feedback
          </div>
          <p className="text-sm text-[#3B5A54] mt-1 max-w-2xl">
            Wrong-slice reports filed by Tender Executives. Use these to spot patterns in Gemini's blind spots and refine the extraction prompt.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-2xl font-black text-[#052A23]" data-testid="ai-feedback-total">{total}</div>
            <div className="text-[10px] uppercase tracking-wide text-[#6D8E87]">Total reports</div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-black text-amber-700" data-testid="ai-feedback-unreviewed">{unreviewed}</div>
            <div className="text-[10px] uppercase tracking-wide text-[#6D8E87]">Unreviewed</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-[#D5E8E4] p-4 mb-4 shadow-sm">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wide text-[#6D8E87] block mb-1">Slot</label>
            <select value={slot} onChange={(e) => setSlot(e.target.value)}
              className="w-full text-sm border border-[#D5E8E4] rounded px-2 py-1.5 bg-white"
              data-testid="ai-feedback-filter-slot">
              <option value="">All slots</option>
              <option value="emd_page">EMD Page</option>
              <option value="participation_criteria">Participation Criteria</option>
              <option value="boq_sheet">BOQ Sheet</option>
            </select>
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wide text-[#6D8E87] block mb-1">Status</label>
            <select value={reviewed} onChange={(e) => setReviewed(e.target.value)}
              className="w-full text-sm border border-[#D5E8E4] rounded px-2 py-1.5 bg-white"
              data-testid="ai-feedback-filter-reviewed">
              <option value="">All</option>
              <option value="false">Unreviewed</option>
              <option value="true">Reviewed</option>
            </select>
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wide text-[#6D8E87] block mb-1">From</label>
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
              className="w-full text-sm border border-[#D5E8E4] rounded px-2 py-1.5 bg-white"
              data-testid="ai-feedback-filter-from" />
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wide text-[#6D8E87] block mb-1">To</label>
            <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
              className="w-full text-sm border border-[#D5E8E4] rounded px-2 py-1.5 bg-white"
              data-testid="ai-feedback-filter-to" />
          </div>
          <div>
            <button type="button" onClick={clearFilters} disabled={!hasFilter}
              className="text-xs px-3 py-1.5 rounded border border-[#D5E8E4] text-[#3B5A54] hover:bg-gray-50 disabled:opacity-40"
              data-testid="ai-feedback-clear-filters">
              Clear filters
            </button>
          </div>
        </div>
        {(rows.length > 0) && (
          <div className="mt-3 flex gap-4 text-[11px] text-[#6D8E87]">
            <span>Showing <b className="text-[#052A23]">{rows.length}</b> report(s)</span>
            <span>EMD: <b className="text-[#052A23]">{slotCounts.emd_page}</b></span>
            <span>Participation: <b className="text-[#052A23]">{slotCounts.participation_criteria}</b></span>
            <span>BOQ: <b className="text-[#052A23]">{slotCounts.boq_sheet}</b></span>
          </div>
        )}
      </div>

      {error && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 text-rose-800 px-3 py-2 text-sm mb-3" data-testid="ai-feedback-error">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-[#D5E8E4] shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F4FAF8] text-[11px] uppercase tracking-wide text-[#6D8E87]">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">Reported</th>
                <th className="text-left px-3 py-2 font-semibold">Slot</th>
                <th className="text-left px-3 py-2 font-semibold">AI pages</th>
                <th className="text-left px-3 py-2 font-semibold">Reporter</th>
                <th className="text-left px-3 py-2 font-semibold">Note</th>
                <th className="text-left px-3 py-2 font-semibold">Files</th>
                <th className="text-left px-3 py-2 font-semibold">Reviewed</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <TableRowsSkeleton cols={7} />
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-10 text-center text-[#6D8E87] text-xs" data-testid="ai-feedback-empty">
                    {hasFilter ? 'No reports match the current filters.' : "No wrong-slice reports yet. When a TE flags a bad AI slice, it'll show up here."}
                  </td>
                </tr>
              )}
              {!loading && rows.map((r) => (
                <React.Fragment key={r.id}>
                <tr className={`border-t border-[#EEF6F3] ${r.reviewed ? 'bg-emerald-50/40' : ''}`} data-testid={`ai-feedback-row-${r.id}`}>
                  <td className="px-3 py-2 whitespace-nowrap text-[#3B5A54]">{fmtDate(r.reported_at)}</td>
                  <td className="px-3 py-2">
                    <span className="inline-flex text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700">
                      {SLOT_LABELS[r.slot] || r.slot}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-[#052A23] font-semibold whitespace-nowrap">{fmtPages(r.ai_pages)}</td>
                  <td className="px-3 py-2 text-[#3B5A54]">{r.reported_by_name || '—'}</td>
                  <td className="px-3 py-2 text-[#3B5A54] max-w-[220px]">
                    <div className="line-clamp-2">{r.note || <span className="text-[#8FA9A3] italic">no note</span>}</div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <div className="flex gap-2">
                      {r.ai_file_url && (
                        <a href={authedFileUrl(r.ai_file_url)} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100"
                          data-testid={`ai-feedback-view-slice-${r.id}`}>
                          <FileText className="w-3.5 h-3.5" /> AI slice <ArrowSquareOut className="w-3 h-3" />
                        </a>
                      )}
                      {r.tender_doc_url && (
                        <a href={authedFileUrl(r.tender_doc_url)} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-[#D5E8E4] text-[#3B5A54] hover:bg-gray-50"
                          data-testid={`ai-feedback-view-source-${r.id}`}>
                          <FileText className="w-3.5 h-3.5" /> Source <ArrowSquareOut className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={() => toggleReviewed(r)} disabled={busyId === r.id}
                        className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded font-semibold disabled:opacity-50 ${
                          r.reviewed
                            ? 'border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100'
                            : 'border border-[#D5E8E4] text-[#3B5A54] hover:bg-gray-50'
                        }`}
                        title={r.reviewed && r.reviewed_at ? `Reviewed on ${fmtDate(r.reviewed_at)}${r.reviewed_by_name ? ' by ' + r.reviewed_by_name : ''}` : 'Mark as reviewed'}
                        data-testid={`ai-feedback-toggle-${r.id}`}>
                        {r.reviewed ? <><CheckCircle weight="fill" className="w-3.5 h-3.5" /> Reviewed</> : <><Circle className="w-3.5 h-3.5" /> Mark reviewed</>}
                      </button>
                      {/* iter105 — Copy as prompt tweak (reviewed rows only) */}
                      {r.reviewed && (
                        <button type="button" onClick={() => onCopyTweakClick(r)}
                          disabled={busyId === r.id || tweakEditingId === r.id}
                          className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded font-semibold border disabled:opacity-50 ${
                            copiedId === r.id
                              ? 'border-emerald-300 bg-emerald-100 text-emerald-800'
                              : 'border-indigo-200 bg-indigo-50 text-indigo-800 hover:bg-indigo-100'
                          }`}
                          title={Array.isArray(r.correct_pages) && r.correct_pages.length > 0
                            ? `Copy prompt tweak (correct pages cached: ${fmtPages(r.correct_pages)})`
                            : 'Copy prompt tweak — will ask for correct pages first'}
                          data-testid={`ai-feedback-copy-tweak-${r.id}`}>
                          <ClipboardText className="w-3.5 h-3.5" /> {copiedId === r.id ? 'Copied!' : 'Prompt tweak'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                {/* iter105 — inline capture row for "correct pages" */}
                {tweakEditingId === r.id && (
                  <tr className="border-t border-indigo-100 bg-indigo-50/60" data-testid={`ai-feedback-tweak-editor-${r.id}`}>
                    <td colSpan={7} className="px-3 py-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-[11px] font-semibold text-indigo-900">
                          What were the correct pages for this "{SLOT_LABELS[r.slot] || r.slot}"?
                        </span>
                        <input type="text" value={tweakInput}
                          onChange={(e) => { setTweakInput(e.target.value); setTweakError(''); }}
                          onKeyDown={(e) => { if (e.key === 'Enter') saveTweakAndCopy(r); if (e.key === 'Escape') setTweakEditingId(''); }}
                          placeholder="e.g. 12-14  or  12,13,14"
                          className="text-sm px-2 py-1 border border-indigo-300 rounded bg-white min-w-[180px]"
                          autoFocus
                          data-testid={`ai-feedback-tweak-input-${r.id}`} />
                        <button type="button" onClick={() => saveTweakAndCopy(r)}
                          disabled={busyId === r.id}
                          className="text-[11px] px-3 py-1 rounded font-semibold bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                          data-testid={`ai-feedback-tweak-save-${r.id}`}>
                          {busyId === r.id ? 'Saving…' : 'Save & copy'}
                        </button>
                        <button type="button" onClick={() => { setTweakEditingId(''); setTweakError(''); }}
                          className="text-[11px] px-2 py-1 rounded border border-indigo-200 text-indigo-800 hover:bg-white"
                          data-testid={`ai-feedback-tweak-cancel-${r.id}`}>
                          Cancel
                        </button>
                        {tweakError && (
                          <span className="text-[11px] text-rose-700 font-semibold" data-testid={`ai-feedback-tweak-error-${r.id}`}>
                            {tweakError}
                          </span>
                        )}
                      </div>
                      <p className="text-[10px] text-indigo-900/70 mt-1">
                        We'll cache these pages on this row and copy a ready-to-paste snippet you can add to the Gemini extraction prompt.
                      </p>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
